// #region IMPORTS
import type { Object3D, SkinnedMesh } from 'three';
import * as THREE from 'three';
import { subscribeKey } from 'valtio/utils';

import ThreeWrapper from '@cartwhl/3d/threejs/ThreeWrapper';
import SkyboxGradientMaterial from '@cartwhl/3d/threejs/materials/SkyboxGradientMaterial';
import { disposeObject3D, traverseFind } from '@cartwhl/3d/threejs/util';
import { poseFromSkeleton } from '@cartwhl/3d/threejs/utils/BVHExporter';
import GizmoControl from '@cartwhl/3d/threejs/utils/GizmoControl';
import type { PickingResult } from '@cartwhl/3d/threejs/utils/ScreenPicker';
import { ScreenPicker, Tasker } from '@cartwhl/3d/threejs/utils/ScreenPicker';
import VisualDebug from '@cartwhl/3d/threejs/utils/VisualDebug';

import {
  characterPickerStore,
  posingStore,
  projectStore,
  sceneSettingsStore,
} from 'stores';
import { GizmoModeType } from 'stores/posingStore';
import { captureAnimatedHandsBaseline } from 'utils/applyHandsPreset';

import Poser from './Poser';
import StateMachine from './StateMachine';
import FKRotMachine from './machines/FKRotMachine';
import LimbMachine from './machines/LimbMachine';
import RootMachine from './machines/RootMachine';
import SpineMachine from './machines/SpineMachine';

// #endregion

const IS_DEV_ENV = process.env.NODE_ENV === 'development';

export default class PosingSystem {
  // #region MAIN

  // REFERENCES
  env: ThreeWrapper;
  grpUI = new THREE.Group(); // Container for 3D UI elements
  grpRenderGen = new THREE.Group(); // Container for tasking custom scene renders

  // SUBSYSTEMS
  gizmo: GizmoControl; // Manage transformation 3d controls
  picker: ScreenPicker; // Perform pixel picking
  poser: Poser; // Mange the IK Rig
  tasker = new Tasker(); // Execute pixel picking tasks mainly
  stateMachine = new StateMachine<PosingSystem>(this);

  // MISC
  characterId: string = ''; // Track the last character loaded into posing
  characterReadySub: (() => void) | null = null; // Extra subscription for ready state
  firstCharacterReady: boolean = false; // Track if the first character has been loaded into the posing system
  subscriptions: Array<() => void> = []; // Collection of unsubscribe callbacks
  vdebug = new VisualDebug(); // Dynamic Points & Lines for visual debugging
  private _skeletonHelper?: THREE.SkeletonHelper; // Reference to skeleton helper

  constructor(env: ThreeWrapper) {
    IS_DEV_ENV && console.log('PosingSystem.init', THREE.REVISION);

    // @ts-expect-error ts(2322) - Ignoring Type Error
    globalThis.debug = this.vdebug; // Access visual debugging globally

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.env = env;
    this.grpRenderGen.visible = false;
    this.grpRenderGen.name = 'RenderGenGroup';
    this.grpUI.name = 'UIGroup';

    this.picker = new ScreenPicker(
      this.env.renderer,
      this.env.scene,
      this.env.camera,
    );

    this.poser = new Poser(this);
    this.gizmo = new GizmoControl(this.env.camera, this.env.renderer.domElement)
      .detachless()
      .setSize(0.45)
      .hide();

    // Add objects into the scene
    this.grpUI.add(this.gizmo, this.vdebug);
    this.env.scene.add(this.grpUI, this.grpRenderGen);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Events / Callbacks

    this.subscriptions.push(
      subscribeKey(
        characterPickerStore.state,
        'activeCharacter',
        this.onActiveCharacter,
      ),

      subscribeKey(posingStore.state, 'gizmoMode', this.onGizmoModeChange),
      subscribeKey(
        posingStore.state,
        'showSkeletonHelper',
        this.onShowSkeletonHelper,
      ),
    );

    // Setup Gizmo
    this.gizmo.onDragStart = this.onGizmoDragStart;
    this.gizmo.onDragEnd = this.onGizmoDragEnd;
    this.gizmo.onMove = this.onGizmoMove;
    this.gizmo.onRotate = this.onGizmoRotate;

    // HMR-safe rehydrate: pick up whatever is already selected in the store
    queueMicrotask(() => this.onActiveCharacter());

    // Pointer Events
    this.env.renderer.domElement.addEventListener(
      'pointerdown',
      this.onPointerDown,
    );

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.stateMachine.reg(
      new LimbMachine(),
      new FKRotMachine(),
      new RootMachine(),
      new SpineMachine(),
    );
  }

  dispose() {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // UnSubscibe
    for (const fn of this.subscriptions) fn();

    if (this.characterReadySub) this.characterReadySub();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Cleanup
    disposeObject3D(this.vdebug);
    disposeObject3D(this.grpUI);

    this.poser.dispose();
    this.gizmo.dispose();

    this.env.renderer.domElement.removeEventListener(
      'pointerdown',
      this.onPointerDown,
    );
  }
  // #endregion

  // #region MISC
  showUI(b: boolean): this {
    this.grpUI.visible = b;
    return this;
  }
  // #endregion

  // #region MISC HANDLERS

  // Runs when active character has been swopped
  onActiveCharacter = () => {
    const char = characterPickerStore.state?.activeCharacter;
    IS_DEV_ENV &&
      console.log('PosingSystem - Active character swapped', char?.PK);

    // Clean up any existing character ready subscription
    if (this.characterReadySub) {
      this.characterReadySub();
      this.characterReadySub = null;
    }

    if (char) {
      // if data already exists ( reloading character ), skip to ready state
      if (char.meshData.current) this.onCharacterReady();
      else {
        // Subscribe to this character's ready state
        this.characterReadySub = subscribeKey(
          char,
          'readyOn',
          this.onCharacterReady,
        );
      }
    }
  };

  // Runs when a character's mesh data is available
  onCharacterReady = async () => {
    IS_DEV_ENV && console.log('PosingSystem - Character Ready has been called');
    this.firstCharacterReady = true;
    const result = this._parseActiveCharacter();

    if (result) {
      const [mesh, charID] = result;
      if (this.poser.loadCharacter(mesh)) {
        this.characterId = charID;
        this.showMeshSkeleton(false);

        // only call fitViewActiveCharacter if we don't have scene settings that overrides it.
        if (sceneSettingsStore.state.cameraSettings.serializedCamera) {
          IS_DEV_ENV &&
            console.log('Scene settings overrides fitViewActiveCharacter');
        } else {
          this.fitViewActiveCharacter(1.4, -0.2, 1.5);
        }

        // Keep helper visibility in sync when character changes
        this.showSkeletonHelper(!!posingStore.state.showSkeletonHelper);

        // Check & hide clothing if the axel model
        this.showAxelClothes(false);

        IS_DEV_ENV && console.log('PosingSystem - Character Ready', charID);
      }
    }
  };

  onPointerDown = (e: PointerEvent) => {
    if (e.button === 2) {
      this.poser.selectors.selectedIndex = -1;
      return;
    }

    if (this.gizmo.isDragging) return;
    this.tasker.push(
      this.picker.pixelTaskFromEvent(e, this.onPixelPick, false),
    );
  };

  onPixelPick = (o: PickingResult) => {
    if (o.id !== -1 && o.id != null) {
      // Perform action based on which mesh was picked
      switch (o.obj) {
        case this.poser.selectors.mesh:
          this.poser.selectors.selectedIndex = o.idx as number;
          break;

        case this.poser.targets.mesh: {
          const sm = this.stateMachine.getCurrent();
          if (o.idx != null && sm instanceof SpineMachine) {
            sm.setTargetIndex(o.idx);
          }
          break;
        }
      }
    }
  };

  // #endregion

  // #region COORDINATE SPACE CONVERTERS
  // Helper matrices and vectors for coordinate space conversions
  private _mParent = new THREE.Matrix4();
  private _mInvParent = new THREE.Matrix4();
  private _rotOnly = new THREE.Matrix4();
  private _qParent = new THREE.Quaternion();
  private _v = new THREE.Vector3();
  private _q = new THREE.Quaternion();

  // Convert a position in grpUI-local space to world space
  public _uiPosToWorld(posArr: number[]): number[] {
    this.grpUI.updateMatrixWorld(true);
    this._mParent.copy(this.grpUI.matrixWorld);
    this._v.fromArray(posArr).applyMatrix4(this._mParent);
    return this._v.toArray();
  }

  // Convert a quaternion in grpUI-local space to world space
  public _uiQuatToWorld(quatArr: number[]): number[] {
    this.grpUI.updateMatrixWorld(true);
    this._rotOnly.extractRotation(this.grpUI.matrixWorld);
    this._qParent.setFromRotationMatrix(this._rotOnly);

    this._q.fromArray(quatArr);
    this._q.premultiply(this._qParent); // world = parent * local
    return this._q.toArray() as number[];
  }

  // Convert a position in world space to grpUI-local
  private _worldPosToUI(posArr: number[]): number[] {
    this.grpUI.updateMatrixWorld(true);
    this._mInvParent.copy(this.grpUI.matrixWorld).invert();
    this._v.fromArray(posArr).applyMatrix4(this._mInvParent);
    return this._v.toArray();
  }

  // Convert a quaternion in world space to grpUI-local
  private _worldQuatToUI(quatArr: number[]): number[] {
    this.grpUI.updateMatrixWorld(true);
    this._rotOnly.extractRotation(this.grpUI.matrixWorld);
    this._qParent.setFromRotationMatrix(this._rotOnly);

    const invParent = this._qParent.clone().invert();
    this._q.fromArray(quatArr);
    this._q.premultiply(invParent); // local = parent^-1 * world
    return this._q.toArray() as number[];
  }
  // #endregion

  // #region GIZMO HANDLERS
  onGizmoDragStart = () => {
    this.env.camControl.enabled = false;
    this.stateMachine.getCurrent()?.onGizmoDragStart();
  };

  onGizmoDragEnd = async () => {
    this.env.camControl.enabled = true;
    this.stateMachine.getCurrent()?.onGizmoDragEnd();
    this.poser.selectors.render();
    await this.poseToHistory();

    // // HMR-safe rehydrate after pose changes (only when project exists)
    // this.rehydrateIfStale();
  };

  onGizmoMove = (v: Array<number>) => {
    this.stateMachine.getCurrent()?.onGizmoTranslate(v);
  };

  onGizmoRotate = (v: Array<number>) => {
    this.stateMachine.getCurrent()?.onGizmoRotate(v);
  };

  onGizmoModeChange = (m: GizmoModeType) => {
    this.gizmo.setMode(m);
    this.stateMachine.getCurrent()?.onGizmoModeChange(m);
  };

  onShowSkeletonHelper = () => {
    this.showMeshSkeleton(!!posingStore.state.showSkeletonHelper);
  };

  // HMR-safe rehydrate: detect and fix stale state
  private rehydrateIfStale() {
    const char = characterPickerStore.state?.activeCharacter;
    const hasMesh = !!char?.meshData?.current;
    const poserReady = !!this.poser?.skel;

    if (hasMesh && !poserReady) {
      // New PosingSystem instance, character already loaded in store - bind now
      this.onCharacterReady();
    } else if (poserReady) {
      // Make sure mesh matches the current posed skeleton
      this.poser.refreshSkeleton();
    }
  }
  // #endregion - GIZMO

  // #region RENDERLOOP

  onPostRender() {
    this.tasker.run();
    // NOTE: Calling update is required to make CameraControls work.
    this.env.camControl.update(0.016);
  }

  // #endregion

  // #region DEBUGGING

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debugPose(pose: any) {
    const d = this.vdebug;

    let c;
    for (const p of pose.bones) {
      d.pnt.push(p.world.pos, 0x303030, 1, 1);
      for (const ci of p.children) {
        c = pose.bones[ci];
        d.ln.push(p.world.pos, c.world.pos, 0x303030);
      }
    }
  }

  // #endregion

  // #region HISTORY

  async poseToHistory(doClear: boolean = false, shouldUpdate: boolean = true) {
    // poseToHistory is called when the page loads, but we don't want it
    // to trigger an updateProjectPose.
    const needsUpdate = posingStore.state.historyStack.length > 0;
    if (doClear) posingStore.historyClear();
    posingStore.historyPush(this.poser.getPoseClone());

    // initial poseToHistory calls should not update the project pose, but subsequent calls should
    if (needsUpdate && shouldUpdate) {
      await this.updateProjectPose();
    }
  }

  async screenshotCanvas(): Promise<string> {
    const canvasImageTaskURL = await this.canvasImageTask();
    const canvasBase64 = canvasImageTaskURL.replace(
      /^data:image\/\w+;base64,/,
      '',
    );
    return canvasBase64;
  }

  async updateProjectPose() {
    const canvasBase64 = await this.screenshotCanvas();
    posingStore.setLastCanvasBase64(canvasBase64);

    // Check if we have a project to update
    await projectStore.createOrUpdatePoseObject(canvasBase64);
  }

  async poseUndoRedo(isUndo = true) {
    const pose = isUndo ? posingStore.historyUndo() : posingStore.historyRedo();
    if (pose) {
      this.poser.setPose(pose);
      this.stateMachine.getCurrent()?.onUndoRedo();

      // Save undo/redo to s3 project
      await new Promise((resolve) => setTimeout(resolve, 100));
      const canvasBase64 = await this.screenshotCanvas();
      posingStore.setLastCanvasBase64(canvasBase64);
      await projectStore.createOrUpdatePoseObject(canvasBase64);
      this.rehydrateIfStale();
    }
  }

  // #endregion

  // #region HELPERS

  /** Find a skinned mesh & id of the active character */
  _parseActiveCharacter(): [SkinnedMesh, string] | null {
    const char = characterPickerStore.state?.activeCharacter;

    if (char) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      // Only allow ready state to character once
      // Exit early if the character has been loaded & rigged
      if (this.characterId === char.PK) return null;

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      const mData = char.meshData.current;
      if (mData) {
        // ---------------------
        // Find the first skinned mesh to get access to skeleton
        const meshes = traverseFind<SkinnedMesh>(
          mData.scene,
          false,
          (o: Object3D) => o.type === 'SkinnedMesh',
        );

        // ---------------------
        if (meshes.length > 0) {
          return [meshes[0], char.PK];
        } else {
          IS_DEV_ENV &&
            console.log(
              'PosingSystem - No skinned meshes found for active character',
            );
        }
      } else {
        IS_DEV_ENV &&
          console.log('PosingSystem - No mesh data available on ready state');
      }
    } else {
      IS_DEV_ENV &&
        console.log(
          'PosingSystem - Error no active character from on ready state',
        );
    }

    return null;
  }

  // #endregion

  // #region SKELETON RELATED

  /** control the skeleton that is part of the character mesh */
  showMeshSkeleton(b: boolean) {
    const ch = characterPickerStore.state.activeCharacter;
    if (ch && ch.meshData.current) {
      ch.meshData.current.scene.traverse((o: Object3D) => {
        // Ignore anything that isn't named skeleton
        if (!o.name.endsWith('_skl')) return;

        // Setup skeleton to render over mesh if first time
        if (o.renderOrder !== 50) {
          o.renderOrder = 50;

          for (const c of o.children) {
            if (
              c &&
              typeof c === 'object' &&
              'material' in c &&
              c.material &&
              typeof c.material === 'object' &&
              'depthTest' in c.material
            ) {
              (c.material as THREE.Material).depthTest = false;
            }
          }
        }

        // Set State
        o.visible = b;
      });
    }
  }

  /** add/remove threejs's skeleton helper in the scene */
  showSkeletonHelper(show: boolean) {
    if (!this.poser.skel) return;

    if (show) {
      if (!this._skeletonHelper) {
        const root = this.poser.skel.bones[0];
        const helper = new THREE.SkeletonHelper(root);
        helper.name = 'PoseSkeletonHelper';
        helper.frustumCulled = false;
        // draw on top of the mesh
        (helper.material as THREE.LineBasicMaterial).depthTest = false;
        (helper.material as THREE.LineBasicMaterial).transparent = true;
        helper.renderOrder = 100;

        this.env.scene.add(helper);
        this._skeletonHelper = helper;
      }
      this._skeletonHelper.visible = true;
    } else {
      if (this._skeletonHelper) this._skeletonHelper.visible = false;
    }
  }

  // #endregion

  // #region CAMERA RELATED

  /** Set camera at optimum location & rotation to display character in viewport */
  fitViewActiveCharacter(scl = 1.4, yOffset = -0.2, distanceMultiplier = 1.5) {
    const ch = characterPickerStore.state.activeCharacter;
    if (ch && ch.meshData.current) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      const box = new THREE.Box3();
      ch.meshData.current.scene.traverse((o: Object3D) => {
        if (o.type !== 'SkinnedMesh') return;

        box.expandByObject(o, false);

        // Debug Mesh Bounding Box
        // const b = (o as any).geometry.boundingBox;
        // this.vdebug.ln.box(b.min.toArray(), b.max.toArray(), 0x0000ff);
      });

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      // Expand/Contract box, ie box.expandByScalar doesnt as expected
      const center = box.getCenter(new THREE.Vector3());
      box.min.sub(center).multiplyScalar(scl).add(center);
      box.max.sub(center).multiplyScalar(scl).add(center);

      // Compute camera data
      const size = box.getSize(new THREE.Vector3());
      const dist = this.fitBoxPerspectiveDistance(
        this.env.camera as THREE.PerspectiveCamera,
        size.x,
        size.y,
        size.z,
      );

      // Add some offset to move camera vertically
      center.y += yOffset;

      // Move forward N distance from bbox center (with distance multiplier)
      const camPos = [center.x, center.y, center.z + dist * distanceMultiplier];

      // Debug Bounding Box
      // this.vdebug.ln.box(box.min.toArray(), box.max.toArray(), 0xff0000);

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      // Apply Position & Look roation
      this.env.camControl.setLookAt(
        camPos[0],
        camPos[1],
        camPos[2],
        center.x,
        center.y,
        center.z,
        false,
      );

      this.env.camControl.saveState();
    }
  }

  /** Set camera at optimum location & rotation to display a pose in viewport */
  fitViewPose(pose: any, scl = 1.4, yOffset = -0.2, distanceMultiplier = 1.5) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const box = new THREE.Box3();
    const v = new THREE.Vector3();
    box.min.set(Infinity, Infinity, Infinity);
    box.max.set(-Infinity, -Infinity, -Infinity);

    pose.updateWorld();

    for (const b of pose.bones) {
      v.fromArray(b.world.pos);
      box.min.min(v);
      box.max.max(v);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Expand/Contract box, ie box.expandByScalar doesnt as expected
    const center = box.getCenter(new THREE.Vector3());
    box.min.sub(center).multiplyScalar(scl).add(center);
    box.max.sub(center).multiplyScalar(scl).add(center);

    // Compute camera data
    const size = box.getSize(new THREE.Vector3());
    const dist = this.fitBoxPerspectiveDistance(
      this.env.camera as THREE.PerspectiveCamera,
      size.x,
      size.y,
      size.z,
    );

    // Add some offset to move camera vertically
    center.y += yOffset;

    // Move forward N distance from bbox center (with distance multiplier)
    const camPos = [center.x, center.y, center.z + dist * distanceMultiplier];

    // Debug Bounding Box
    // this.vdebug.ln.box(box.min.toArray(), box.max.toArray(), 0xff0000);
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Apply Position & Look roation
    this.env.camControl.setLookAt(
      camPos[0],
      camPos[1],
      camPos[2],
      center.x,
      center.y,
      center.z,
      true,
    );
    this.env.camControl.saveState();
  }

  // #endregion

  // #region TASKS

  /** Render the scene without the UI visible to a png dataURL */
  canvasImageTask(
    props: {
      showAxes?: boolean;
      showAxelClothes?: boolean;
      showGrid?: boolean;
      showDome?: boolean;
      domeColorRamp?: Array<string | number>;
    } = {},
  ): Promise<string> {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Setup Promise
    let refResolve: (v: string) => void;
    let refReject: (err: Error) => void;

    const promise = new Promise<string>((resolve, reject) => {
      refResolve = resolve;
      refReject = reject;
    });

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const opts = {
      showAxes: false,
      showAxelClothes: false,
      showGrid: false,
      showDome: false,
      // ORIGINAL BLUE [ 0, '#DCDCDC', 0.3, '#DCDCDC', 0.7, '#60B8D3', 1, '#60B8D3' ]
      domeColorRamp: [
        0,
        '#DCDCDC',
        0.3,
        '#DCDCDC',
        0.7,
        '#303030',
        1,
        '#303030',
      ],
      ...props,
    };

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.tasker.push(() => {
      // ----------------------------
      this.grpRenderGen.visible = true;
      if (opts.showAxes) this.poser.renderAxes();
      if (opts.showAxelClothes) this.showAxelClothes(true);
      if (opts.showGrid)
        this.showTempGridFloor(this.env.scene, this.grpRenderGen);
      if (opts.showDome)
        this.showTempSky(this.grpRenderGen, opts.domeColorRamp);

      // ----------------------------
      // Render scene without UI
      this.showUI(false);
      this.env.render();

      // ----------------------------
      // Capture the scene as an image
      let dataUrl;
      try {
        dataUrl = this.env.renderer.domElement.toDataURL('image/png');
      } catch (err: unknown) {
        refReject(err as Error);
      }

      // ----------------------------
      // Cleanup
      this.grpRenderGen.visible = false;
      for (const obj of this.grpRenderGen.children) {
        obj.visible = false;
      }
      if (opts.showAxelClothes) this.showAxelClothes(false);

      // ----------------------------
      // ReRender the scene with the UI
      this.showUI(true);
      this.env.render();

      if (dataUrl) refResolve(dataUrl);
    });

    return promise;
  }

  // #endregion

  // #region HELPER FUNCTIONS
  fitBoxPerspectiveDistance(
    camera: THREE.PerspectiveCamera,
    width: number,
    height: number,
    depth: number,
    cover = false,
  ): number {
    // Cover tries to fill width more then height
    const fov = camera.getEffectiveFOV() * 0.01745329251; // FOV in Radians
    const camRatio = camera.aspect;
    const boxRatio = width / height;

    const fitSize = (cover ? boxRatio > camRatio : boxRatio < camRatio)
      ? height
      : width / camRatio;
    return (fitSize * 0.5) / Math.tan(fov * 0.5) + depth * 0.5;
  }

  showTempGridFloor(scene: THREE.Scene, container: THREE.Group) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Check if the scene's grid floor is currently visible, exit out early on exist
    const drei = traverseFind<THREE.Mesh>(
      scene,
      false,
      (o: Object3D) => o.name === 'DreiGrid',
    );

    if (drei.length > 0) return;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Load in a temporary grid
    const ary = traverseFind<THREE.Mesh>(
      container,
      false,
      (o: Object3D) => o.name === 'TempGridFloor',
    );

    if (ary.length > 0) {
      ary[0].visible = true;
    } else {
      const grid = new THREE.GridHelper(36, 36);
      grid.name = 'TempGridFloor';
      container.add(grid);
    }
  }

  showTempSky(container: THREE.Group, ramp: Array<string | number>) {
    // Find if object exists in container
    const ary = traverseFind<THREE.Mesh>(
      container,
      false,
      (o: Object3D) => o.name === 'TempSkyDome',
    );

    // Update object
    if (ary.length > 0) {
      const material = ary[0].material;
      if (material && typeof material === 'object' && 'setRamp' in material) {
        (material as SkyboxGradientMaterial).setRamp(ramp);
      }
      ary[0].visible = true;
    } else {
      // Create new object
      const sky = SkyboxGradientMaterial.createDome(490);
      sky.name = 'TempSkyDome';
      if (
        sky.material &&
        typeof sky.material === 'object' &&
        'setRamp' in sky.material
      ) {
        (sky.material as SkyboxGradientMaterial).setRamp(ramp);
      }
      container.add(sky);
    }
  }

  showAxelClothes(isVisible: boolean = false) {
    if (characterPickerStore.state.activeCharacter?.PK === 'char-upload-Axel') {
      const char = characterPickerStore.state.activeCharacter;
      if (char) {
        if (char.meshData.current) {
          char.meshData.current.scene.traverse((obj: THREE.Object3D) => {
            if (obj.name.indexOf('_RND') > -1) {
              obj.visible = isVisible;
            }
          });
        }
      }
    }
  }
  // #endregion
}
